/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import addProductInformationFilter from "@stoneage/App/Handlers/Documents/addProductInformationFilter";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    pagination: state.xProductInfo.data?.pagination,
});

const mapDispatchToProps = {
    addProductInformationFilter,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListSortSelectStyles {
    wrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

export const sortSelectStyles: ProductListSortSelectStyles = {
    wrapper: {
        css: css`
            & .form-input-wrapper {
                width: 143px;
            }
            && select {
                height: 26px;
            }
            && select + span {
                width: 22px;
                top: -2px;
            }

            && label {
                padding: 0 8px 0 0;
            }
        `,
    },
    select: {
        mergeCss: true,
        css: css`
            & > span {
                width: 22px;
            }
        `,
        labelProps: {
            css: css`
                width: unset;
            `,
        },
        cssOverrides: {
            formField: css`
                & > span {
                    width: 22px;
                }
            `,
        },
    },
};

const styles = sortSelectStyles;

const ProductInformationSortSelect = ({ pagination, addProductInformationFilter }: Props) => {
    if (!pagination || !pagination.totalItemCount) {
        return null;
    }

    const onChangeSortHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        addProductInformationFilter({ filterType: "SortType", value: event.currentTarget.value });
    };

    return (
        <StyledWrapper {...styles.wrapper} className="select-wrapper">
            <Select
                {...styles.select}
                label={translate("Sort by")}
                iconProps={{
                    size: 2,
                    src: "IconArrowDropdownSmall",
                }}
                value={pagination.sortType}
                labelPosition="left"
                onChange={onChangeSortHandler}
                data-test-selector="productListSortSelect"
            >
                {pagination.sortOptions.map(sortOption => (
                    <option key={sortOption.sortType} value={sortOption.sortType}>
                        {sortOption.displayName}
                    </option>
                ))}
            </Select>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductInformationSortSelect),
    definition: {
        group: "Product Information" as any,
        displayName: "Sort Select",
        allowedContexts: ["ProductInformationPage"] as any,
    },
};

export default widgetModule;
